import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Check, Users } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { periodText } from "../../lib/courseMeta";
/**
 * Auteurs d'un cours ou d'une analyse : un ou plusieurs admins (au moins un).
 * value : liste d'identifiants ; vide = l'admin connecté (c'est lui qui sera enregistré comme auteur).
 * Les noms s'affichent toujours dans l'ordre de la liste des admins : « Par Skebodza et Karma ».
 */
export function AuthorPicker({ value = [], onChange, currentUserId, testid = "author-picker" }) {
  const { data: authors = [] } = useQuery({
    queryKey: ["admin-authors"],
    queryFn: async () => (await api.get("/admin/authors")).data,
    staleTime: 5 * 60 * 1000,
  });
  const selected = value.length ? value : currentUserId ? [currentUserId] : [];
  const chosen = authors.filter((a) => selected.includes(a.id));
  const label = chosen.length <= 1
    ? chosen[0]?.pseudo || "—"
    : `${chosen.slice(0, -1).map((a) => a.pseudo).join(", ")} et ${chosen[chosen.length - 1].pseudo}`;
  const toggle = (id) => {
    const on = selected.includes(id);
    if (on && selected.length === 1) {
      toast.error("Il faut au moins un auteur");
      return;
    }
    const next = on ? selected.filter((x) => x !== id) : [...selected, id];
    onChange(authors.map((a) => a.id).filter((x) => next.includes(x)));
  };
  return (
    <div className="grid gap-2" data-testid={testid}>
      <div className="text-xs text-zinc-500 inline-flex items-center gap-1.5">
        <Users className="w-3.5 h-3.5" /> Auteurs
      </div>
      <div className="flex flex-wrap gap-2">
        {authors.map((a) => {
          const on = selected.includes(a.id);
          return (
            <button
              key={a.id}
              type="button"
              onClick={() => toggle(a.id)}
              aria-pressed={on}
              className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-full border text-sm transition-colors ${
                on ? "border-emerald-500/50 bg-emerald-500/15 text-emerald-300" : "border-white/10 text-zinc-400 hover:text-white"
              }`}
              data-testid={`${testid}-${a.pseudo}`}
            >
              {on && <Check className="w-3.5 h-3.5" />}
              {a.pseudo}
            </button>
          );
        })}
      </div>
      <div className="text-xs text-zinc-500">
        Sur le site : <span className="text-zinc-300" data-testid={`${testid}-label`}>Par {label}</span>
      </div>
    </div>
  );
}
// Champ année : nombre saisi (sans signe) + case « av. J.-C. » → nombre négatif avant J.-C.
const toField = (y) => ({ text: typeof y === "number" ? String(Math.abs(y)) : "", bc: typeof y === "number" && y < 0 });
const fromField = (f) => {
  const n = parseInt(String(f.text).replace(/\D/g, ""), 10);
  return n > 0 ? (f.bc ? -n : n) : null;
};
function YearInput({ label, optional, field, onChange, testid }) {
  return (
    <div className="grid gap-1.5">
      <span className="text-[11px] text-zinc-500">
        {label}
        {optional && <span className="text-zinc-600"> (facultatif)</span>}
      </span>
      <div className="flex items-center gap-2">
        <input
          inputMode="numeric"
          value={field.text}
          onChange={(e) => onChange({ ...field, text: e.target.value.replace(/\D/g, "").slice(0, 5) })}
          placeholder={optional ? "—" : "2008"}
          className="w-20 h-9 rounded-md bg-white/[0.03] border border-white/10 px-3 text-sm font-mono-num outline-none focus:border-emerald-500/50"
          data-testid={`${testid}-year`}
        />
        <label className="inline-flex items-center gap-1.5 text-xs text-zinc-300 cursor-pointer select-none whitespace-nowrap">
          <input
            type="checkbox"
            checked={field.bc}
            onChange={(e) => onChange({ ...field, bc: e.target.checked })}
            className="accent-emerald-500 w-4 h-4"
            data-testid={`${testid}-bc`}
          />
          av. J.-C.
        </label>
      </div>
    </div>
  );
}
/**
 * Date d'une rétrospective : année (obligatoire pour la placer sur la frise) et année de fin facultative.
 * onChange({ year_start, year_end }) avec des nombres (négatifs avant J.-C.) ou null.
 */
export function RetroDateFields({ start, end, onChange }) {
  const [s, setS] = useState(() => toField(start));
  const [e, setE] = useState(() => toField(end));
  // Valeurs changées de l'extérieur (chargement du cours, brouillon récupéré)
  useEffect(() => {
    if (fromField(s) !== (start ?? null)) setS(toField(start));
    if (fromField(e) !== (end ?? null)) setE(toField(end));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [start, end]);
  const emit = (ns, ne) => {
    setS(ns);
    setE(ne);
    onChange({ year_start: fromField(ns), year_end: fromField(ne) });
  };
  const ys = fromField(s);
  const ye = fromField(e);
  const error = ys != null && ye != null && ye < ys;
  return (
    <div className="grid gap-2" data-testid="course-form-period">
      <span className="text-xs text-zinc-500">Date de l'événement</span>
      <div className="grid grid-cols-2 gap-3">
        <YearInput label="Année" field={s} onChange={(f) => emit(f, e)} testid="period-start" />
        <YearInput label="Jusqu'à" optional field={e} onChange={(f) => emit(s, f)} testid="period-end" />
      </div>
      <div className="rounded-md bg-white/[0.03] border border-white/5 px-3 py-2 text-xs text-zinc-400">
        Affiché : <b className="text-orange-300 font-mono" data-testid="period-preview">{ys == null ? "—" : periodText(ys, error ? null : ye)}</b>
      </div>
      {error ? (
        <p className="text-[11px] text-rose-400">L'année de fin doit venir après l'année de début.</p>
      ) : (
        <p className="text-[11px] text-zinc-500">Place la rétrospective sur la frise. Pour une période, remplissez aussi « Jusqu'à ».</p>
      )}
    </div>
  );
}
